// #766 Story E View 6 — mood × card outcome matrix.
//
// Rows: card_keys (sorted by total sample count desc).
// Columns: mood states (cheerful / neutral / quiet / low).
// Cells: average engagement_score when that card was played to a
// user in that mood, plus the sample count "67 (n=23)".
// Colour-code: high engagement green, low red, low-sample-size grey.
//
// Filters: language, cohort (cold_start / steady_state), days.
//
// Decision the view informs: which cards land in which moods?
// Empirical input for tuning Story A's mood-discount factors.
import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { userSubPageStyles, adminFetch } from './userSubPageStyles';
import { ENGAGEMENT_LABEL } from './labels';

// v1.1.0 — #927: page-subtitle normalised from "Avg engagement_score…"
// to the canonical `Engagement` term sourced from `./labels.js`.
// Backend column `card_outcomes.engagement_score` unchanged.
const VERSION = 'v1.1.0';

const COHORT_OPTIONS = [
  { value: '', label: 'All users' },
  { value: 'cold_start', label: 'Cold-start' },
  { value: 'steady_state', label: 'Steady state' },
];

const RANGE_OPTIONS = [
  { value: '', label: 'All time' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

function cellStyle(avg, n, minSamples) {
  // Low sample size — grey out for low confidence.
  if (n < minSamples) {
    return { background: '#F5F5F5', color: '#aaa', fontStyle: 'italic' };
  }
  if (avg == null) return { background: '#F5F5F5', color: '#aaa' };
  // High engagement green, mid coral, low red.
  if (avg >= 70) return { background: '#E8F5E9', color: '#2E7D32', fontWeight: 700 };
  if (avg >= 40) return { background: '#FFF3E0', color: '#E65100', fontWeight: 600 };
  return { background: '#FFEBEE', color: '#C62828', fontWeight: 700 };
}

export default function AdminCardsMoodMatrix() {
  const [language, setLanguage] = useState('');
  const [cohort, setCohort] = useState('');
  const [days, setDays] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (language) qs.set('language', language);
        if (cohort) qs.set('cohort', cohort);
        if (days) qs.set('days', String(days));
        const url = `/api/admin/cards/mood-matrix${qs.toString() ? `?${qs}` : ''}`;
        const r = await adminFetch(url);
        if (cancelled) return;
        setData(r || { moods: [], rows: [] });
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [language, cohort, days]);

  const moods = data?.moods || ['cheerful', 'neutral', 'quiet', 'low'];
  const rows = data?.rows || [];
  const minSamples = data?.min_samples ?? 5;

  // Sort rows by max-engagement cell desc (most-engaging card-types first),
  // with low-sample-only rows pushed to the bottom.
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      // Primary: total sample count desc.
      return b.total_n - a.total_n;
    });
  }, [rows]);

  return (
    <AdminLayout title="Cards · Mood matrix">
      <style>{userSubPageStyles}</style>

      <div className="page-header">
        <div className="page-title">
          Mood × card matrix<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Avg {ENGAGEMENT_LABEL.toLowerCase()} when card played to user in mood state ·
          {' '}cells with n &lt; {minSamples} greyed out · #766 Story E View 6
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18,
        flexWrap: 'wrap',
      }}>
        <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600 }}>Language</label>
        <input type="text" value={language} placeholder="(all)"
               onChange={e => setLanguage(e.target.value.trim().toLowerCase())}
               style={{ width: 100, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }} />
        <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600 }}>Cohort</label>
        <select value={cohort} onChange={e => setCohort(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }}>
          {COHORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <label style={{ fontSize: '0.82rem', color: '#666', fontWeight: 600 }}>Range</label>
        <select value={days} onChange={e => setDays(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }}>
          {RANGE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && !error && rows.length === 0 && (
        <div className="empty-state">No data for these filters.</div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="data-grid">
          <table className="data-table" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Card key</th>
                {moods.map(m => (
                  <th key={m} style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                    {m}
                  </th>
                ))}
                <th style={{ textAlign: 'right' }}>Total n</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(r => (
                <tr key={r.card_key}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}>
                    {/* #806 — open_thread cards have UUID suffixes;
                        backend pre-joins display_label so admins
                        can see what the recall was about instead of
                        a raw GUID. Falls through to card_key when
                        display_label is null (every other card type). */}
                    {r.display_label || r.card_key}
                  </td>
                  {moods.map(m => {
                    const cell = r.by_mood[m];
                    const avg = cell?.avg;
                    const n = cell?.n ?? 0;
                    return (
                      <td key={m} style={{
                        textAlign: 'center', padding: '8px 6px',
                        ...cellStyle(avg, n, minSamples),
                      }}
                          title={`${n} samples`}>
                        {n === 0 ? '—' : (avg != null ? Math.round(avg) : '—')}
                        <div style={{
                          fontSize: '0.7rem', color: 'inherit', opacity: 0.7,
                          marginTop: 2, fontWeight: 400, fontStyle: 'normal',
                        }}>n={n}</div>
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'right', color: '#888' }}>{r.total_n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <p style={{ marginTop: 14, color: '#888', fontSize: '0.82rem' }}>
          <strong>Legend:</strong> <span style={{
            background: '#E8F5E9', color: '#2E7D32', padding: '2px 6px',
            borderRadius: 4, fontWeight: 700,
          }}>≥70</span> high engagement · <span style={{
            background: '#FFF3E0', color: '#E65100', padding: '2px 6px',
            borderRadius: 4, fontWeight: 600,
          }}>40–69</span> mid · <span style={{
            background: '#FFEBEE', color: '#C62828', padding: '2px 6px',
            borderRadius: 4, fontWeight: 700,
          }}>&lt;40</span> low · <span style={{
            background: '#F5F5F5', color: '#aaa', padding: '2px 6px',
            borderRadius: 4, fontStyle: 'italic',
          }}>n&lt;{minSamples}</span> low confidence
        </p>
      )}
    </AdminLayout>
  );
}
